import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import { 
  AlertZone, 
  PatientSummary, 
  Utox,
  SurveyGroup,
  ParticipantSynopsisResponse
} from "@/views/summary/summary.types";
import { ClientDDL, ClientInfo } from "@/views/base/base.types";
import { ErrorBag, Field } from "vee-validate";
import tinycolor from "tinycolor2";

@Component({
  computed: {
    ...mapState("Summary", [
      "phq9AlertZones",
      "gad7AlertZones",
      "sowsAlertZones",
      "utoxIcon",
      "surveyGroups",
      "participantSynopsisResponse"
    ]),
    ...mapState("Base", [
      "selectedClientDDL",
      "patientSummaries",
      "clientInfo"
    ]),
    ...mapFields("Summary", [
      "sortBy", 
      "sortDesc", 
      "searchQuery",
      "selectedPatientSummary",
      "patientSummaries"
    ])
  }
})
export default class SummaryMixin extends Vue {
  selectedPatientSummary!: PatientSummary;
  patientSummaries!: PatientSummary[];
  phq9AlertZones!: AlertZone[];
  gad7AlertZones!: AlertZone[];
  sowsAlertZones!: AlertZone[];
  surveyGroups!: SurveyGroup[];
  selectedClientDDL!: ClientDDL;
  clientInfo!: ClientInfo[];
  utoxIcon!: Utox;
  participantSynopsisResponse!: ParticipantSynopsisResponse;

  sortBy!: string;
  sortDesc!: boolean;
  searchQuery!: string;

  veeFields!: Field;
  veeErrors!: ErrorBag;

  fields = [
    {
      key: 'aiInsight',
      label: 'AI Insight'
    },
    {
      key: 'watch',
      label: 'Has Watch'
    },
    {
      key: 'mrn',
      label: 'MRN #'
    },
    {
      key: 'firstName',
      label: 'First Name'
    },
    {
      key: 'lastName',
      label: 'Last Name'
    },
    // {
    //   key: 'msi',
    //   label: 'MSI'
    // },
    {
      key: 'groupNames',
      label: 'E-Gage Plan'
    },
    {
      key: 'stressScore',
      label: 'BSI'
    },
    {
      key: 'phq9',
      label: 'PHQ-9'
    },
    {
      key: 'gad7',
      label: 'GAD-7'
    },
    {
      key: 'sows',
      label: 'SOWS'
    },
    {
      key: 'labStatus',
      label: 'Utox'
    },
    {
      key: 'appointmentColor',
      label: 'Appointment'
    },
    {
      key: 'sleep',
      label: 'Sleep Duration'
    }
  ]

  get selectedClient() {
    return this.clientInfo.find(
      item => item.clientId === this.selectedClientDDL.value
    ) ?? new ClientInfo;
  }

  getColor(index: number, alertZones: AlertZone[]) {
    const alertZone = alertZones.filter(e => e.startZone <= index && e.endZone >= index);
    if (alertZone.length > 0) {
      return tinycolor(alertZone[0].color).darken(25).toHexString();
    }
    return "#FFFFFF";
  }

  getObservationDate(participantId: number) {
    return this.patientSummaries.find(
      item => item.participantId === participantId
    )?.observationDate ?? "";
  }

  formatSuperset(valueChange: number) {
    if (valueChange === 0) return '';
    return valueChange > 0 ? `+${valueChange}` : `${valueChange}`;
  }

  getSupersetClass(valueChange: number) {
    return valueChange > 0 ? 'positive' : 'negative';
  }

  getSleepColor(value: number) {
    if (value >= 85) return 'low-severity';
    return 'high-severity';
  }

  getUtoxColor(value: boolean) {
    return value ? 'positive' : 'negative';
  }

  getSsiColor(value: number) {
    return value > 66 ? '#f33333' :
      value > 33 && value <= 66 ? '#edb850' :
      '#83b05f'
  }

  getUtoxSupersetColor(value: boolean) {
    return value ? 'high-severity' : 'low-severity';
  }
}